import {
  ExtendedSearchModel,
  FolderSortInvoiceOptions,
  SignatureDisplayStatus,
  UploadFile,
} from '@rentguru/commons-utils';

import { FolderDatabaseModel } from './folderContextUtils';

export enum SyncingType {
  FILES = 'FILES',
  CATEGORIES = 'CATEGORIES',
}

export interface FolderWithDBState {
  initialized: boolean;
  folderDbVersion: number;
  isSynced: boolean;
  isBusy: boolean;
  localBusy: boolean;
  seeAllFolders: boolean;
  onPage: boolean;
  pauseFetching: boolean;
  listView: boolean;
  folderNavigation: (FolderDatabaseModel | undefined)[];
  currentFolders: FolderDatabaseModel[];
  uploadingFiles: UploadFile[];
  extendedSearchModel: ExtendedSearchModel;
  searchFolders: boolean;
  entityDepth: FolderDatabaseModel[];
  selectedFolders: FolderDatabaseModel[];
  blockedFolders: string[];
  draggingFolders: string[];
  sortType: string[];
  invoicePaidSort: FolderSortInvoiceOptions[];
  signatureStatusSort: SignatureDisplayStatus[];
  isSyncing: boolean;
}

export type FolderFilters = Pick<FolderWithDBState, 'sortType' | 'invoicePaidSort' | 'signatureStatusSort'>;

export const initialState: FolderWithDBState = {
  initialized: false,
  folderDbVersion: 0,
  isSynced: false,
  isBusy: false,
  localBusy: false,
  onPage: false,
  seeAllFolders: false,
  searchFolders: false,
  pauseFetching: false,
  listView: true,
  folderNavigation: [undefined],
  currentFolders: [],
  uploadingFiles: [],
  extendedSearchModel: {},
  entityDepth: [],
  selectedFolders: [],
  blockedFolders: [],
  draggingFolders: [],
  sortType: [],
  invoicePaidSort: [],
  signatureStatusSort: [],
  isSyncing: false,
};

type Action =
  | { type: 'SET_IS_BUSY'; payload: boolean }
  | { type: 'SET_IS_SYNCED'; payload: boolean }
  | { type: 'SET_FOLDER_NAVIGATION'; payload: (FolderDatabaseModel | undefined)[] }
  | { type: 'SET_CURRENT_FOLDERS'; payload: FolderDatabaseModel[] }
  | { type: 'SET_UPLOADING_FILES'; payload: UploadFile[] }
  | { type: 'SET_SEE_ALL_FOLDERS'; payload: boolean }
  | { type: 'SET_EXTENDED_SEARCH_MODEL'; payload: ExtendedSearchModel }
  | { type: 'SET_SEARCH_FOLDERS'; payload: boolean }
  | { type: 'SET_ENTITY_DEPTH'; payload: FolderDatabaseModel[] }
  | { type: 'SET_ON_PAGE'; payload: boolean }
  | { type: 'SET_SELECTED_FOLDERS'; payload: FolderDatabaseModel[] }
  | { type: 'SET_PAUSE_FETCHING'; payload: boolean }
  | { type: 'SET_BLOCKED_FOLDERS'; payload: string[] }
  | { type: 'SET_DRAGGING_FOLDERS'; payload: string[] }
  | { type: 'SET_SORT_TYPE'; payload: string }
  | { type: 'RESET_SORT_TYPE' }
  | { type: 'SET_SORT_ON_INVOICE_PAID'; payload: FolderSortInvoiceOptions }
  | { type: 'RESET_SORT_ON_INVOICE_PAID' }
  | { type: 'SET_SORT_ON_SIGNATURE_STATUS'; payload: SignatureDisplayStatus }
  | { type: 'RESET_SORT_ON_SIGNATURE_STATUS' }
  | { type: 'SET_FOLDER_FILTERS'; payload: FolderFilters }
  | { type: 'SET_LIST_VIEW'; payload: boolean }
  | { type: 'SET_INITIALIZED'; payload: boolean }
  | { type: 'INCREMENT_FOLDER_DB_VERSION' }
  | { type: 'SET_LOCAL_BUSY'; payload: boolean }
  | { type: 'SET_IS_SYNCING'; payload: boolean };

export const folderWithDBReducer = (state: FolderWithDBState, action: Action): FolderWithDBState => {
  switch (action.type) {
    case 'SET_IS_BUSY':
      return {
        ...state,
        isBusy: action.payload,
      };
    case 'SET_IS_SYNCED':
      return {
        ...state,
        isSynced: action.payload,
      };
    case 'SET_FOLDER_NAVIGATION':
      return {
        ...state,
        folderNavigation: action.payload,
      };
    case 'SET_CURRENT_FOLDERS':
      return {
        ...state,
        currentFolders: action.payload,
      };
    case 'SET_UPLOADING_FILES':
      return {
        ...state,
        uploadingFiles: action.payload,
      };
    case 'SET_SEE_ALL_FOLDERS':
      return {
        ...state,
        seeAllFolders: action.payload,
      };
    case 'SET_EXTENDED_SEARCH_MODEL':
      return {
        ...state,
        extendedSearchModel: action.payload,
      };
    case 'SET_SEARCH_FOLDERS':
      return {
        ...state,
        searchFolders: action.payload,
      };
    case 'SET_ENTITY_DEPTH':
      return {
        ...state,
        entityDepth: action.payload,
      };
    case 'SET_ON_PAGE':
      return {
        ...state,
        onPage: action.payload,
      };
    case 'SET_SELECTED_FOLDERS':
      return {
        ...state,
        selectedFolders: action.payload,
      };
    case 'SET_PAUSE_FETCHING':
      return {
        ...state,
        pauseFetching: action.payload,
      };
    case 'SET_BLOCKED_FOLDERS':
      return {
        ...state,
        blockedFolders: action.payload,
      };
    case 'SET_DRAGGING_FOLDERS':
      return {
        ...state,
        draggingFolders: action.payload,
      };
    case 'SET_SORT_TYPE':
      const newSortTypes = [...state.sortType];
      if (newSortTypes.includes(action.payload)) {
        return {
          ...state,
          sortType: newSortTypes.filter((sort) => sort !== action.payload),
        };
      }
      newSortTypes.push(action.payload);
      return {
        ...state,
        sortType: newSortTypes,
      };
    case 'RESET_SORT_TYPE':
      return {
        ...state,
        sortType: [],
      };
    case 'SET_SORT_ON_INVOICE_PAID':
      const newInvoicePaidSortTypes = [...state.invoicePaidSort];
      if (newInvoicePaidSortTypes.includes(action.payload)) {
        return {
          ...state,
          invoicePaidSort: newInvoicePaidSortTypes.filter((sort) => sort !== action.payload),
        };
      }
      newInvoicePaidSortTypes.push(action.payload);
      return {
        ...state,
        invoicePaidSort: newInvoicePaidSortTypes,
      };
    case 'RESET_SORT_ON_INVOICE_PAID':
      return {
        ...state,
        invoicePaidSort: [],
      };
    case 'SET_SORT_ON_SIGNATURE_STATUS':
      const newSignatureStatusSort = [...state.signatureStatusSort];
      if (newSignatureStatusSort.includes(action.payload)) {
        return {
          ...state,
          signatureStatusSort: newSignatureStatusSort.filter((sort) => sort !== action.payload),
        };
      }
      newSignatureStatusSort.push(action.payload);
      return {
        ...state,
        signatureStatusSort: newSignatureStatusSort,
      };
    case 'RESET_SORT_ON_SIGNATURE_STATUS':
      return {
        ...state,
        signatureStatusSort: [],
      };
    case 'SET_FOLDER_FILTERS':
      return {
        ...state,
        sortType: action.payload.sortType,
        invoicePaidSort: action.payload.invoicePaidSort,
        signatureStatusSort: action.payload.signatureStatusSort,
      };
    case 'SET_LIST_VIEW':
      return {
        ...state,
        listView: action.payload,
      };
    case 'SET_INITIALIZED':
      return {
        ...state,
        initialized: action.payload,
      };
    case 'INCREMENT_FOLDER_DB_VERSION':
      return {
        ...state,
        folderDbVersion: state.folderDbVersion + 1,
      };
    case 'SET_LOCAL_BUSY': {
      return {
        ...state,
        localBusy: action.payload,
      };
    }
    case 'SET_IS_SYNCING': {
      return {
        ...state,
        isSyncing: action.payload,
      };
    }
    default:
      return state;
  }
};
